import { Component, DestroyRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { AuthService } from '../../../../../@core/auth/auth.service';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { SessionService } from '../../../../../@core/utils/session.service';
import { ConfirmationData, ConfirmationDialogComponent } from '../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { CropVariety, CropVarietyCategory, VarietyCustomField } from '../../../models/crop-variety';
import { CropService } from '../../../services/crop.service';
import { VarietyService } from '../../../services/variety.service';
import { PlatformConfigService } from '../../../services/platform-config.service';
import { Crop } from '../../../models/crop';
import { KwFilterDropdownService } from '../../../widgets/kw-filter-dropdown/kw-filter-dropdown.service';

@Component({
  selector: 'varieties-detail',
  templateUrl: './varieties-detail.component.html',
  styleUrls: ['./varieties-detail.component.scss']
})
export class VarietiesDetailComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  //#region Properties
  // dataSource: PoolListDataSource;
  private varietyId: number;
  public crops: Crop[] = [];
  public variety: CropVariety = {};
  public loading: boolean = true;
  public cropLoading: boolean = true;
  public submitting: any;
  public isEditBtnClick: boolean;
  public showRecheckInterval: boolean = false;
  public categories: CropVarietyCategory[] = [];
  public newCustomField: VarietyCustomField = {
    name: "",
    value: "",
    type: ""
  };
  isColorPickerOpen: boolean = false;
  //#endregion

   //#endregion icon
  public faTrash = faTrash;
  public faPlus = faPlus;
   //#endregion

  //#region ViewChild Properties
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  @ViewChild(MatSort, { static: false }) sort: MatSort;
  //#endregion

  constructor(
    private route: ActivatedRoute,
    private varietyService: VarietyService,
    private errorService: ErrorService,
    private authService: AuthService,
    private toastr: NbToastrService,
    private cropService: CropService,
    private platformConfigService: PlatformConfigService,
    private filterService: KwFilterDropdownService,
    public dialogRef: MatDialogRef<any>,
    private sessionService: SessionService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogService: MatDialog
  ) { 
    this.isEditBtnClick = data.isEditBtnClick;
  }

  ngOnInit() {
    // this.dataSource = new PoolListDataSource(this.paginator, this.sort);
    // get variety id from route
    this.varietyId = +this.route.snapshot.paramMap.get('id') || 0;

    this.variety = this.sessionService.selectedcropVarieties;

    if (this.variety === null || this.variety === undefined) {
      this.variety = {};
      this.variety.isActive = true;
      this.cropLoading = false;
    }

    this.varietyService.getCategories().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
      this.categories = x;
    });

    this.platformConfigService.getShowVarietyRecheckInterval().subscribe(show => {
      this.showRecheckInterval = show;
    });

    this.cropService.getCrops().subscribe(crops => {
      this.crops = crops;
      this.cropLoading = false;
    });

    if ((this.varietyId || 0) !== 0) {
      this.varietyService.getCropVariety(this.varietyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:vrt => {
        this.variety = vrt;
        this.loading = false;
        this.cropLoading = false;
      }, error:err => { this.errorService.handleError(err) }});
    } 
    else {
      this.variety.isActive = true;
      this.cropLoading = false;
    }
  }

  private mergeCustomFields(current: VarietyCustomField[], incoming: VarietyCustomField | VarietyCustomField[]): VarietyCustomField[] {
    const merged = (current || []).slice();
    const items = Array.isArray(incoming) ? incoming : (incoming ? [incoming] : []);
    items.forEach(item => {
      const idx = (item && item.id != null) ? merged.findIndex(f => f.id === item.id) : -1;
      if (idx !== -1) { merged[idx] = item; } else if (item) { merged.push(item); }
    });
    return merged;
  }

  addCustomField(){
    this.loading = true;
    this.newCustomField.cropVarietyId = this.variety.id;
    const addedFieldName = this.newCustomField.name;

    this.varietyService.postCustomField(this.newCustomField).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (x) => {
        this.variety.customFields = this.mergeCustomFields(this.variety.customFields, x);
        this.toastr.success(addedFieldName + ' successfully added!');
        this.newCustomField = {
          name: "",
          value: "",
          type: ""
        }
        this.loading = false;
      },
      error: (err) => {
        this.errorService.handleError(err);
        this.toastr.danger('Failed to add "' + addedFieldName + '". Please try again.', "Add failed");
        this.loading = false;
      }
    })
  }

  confirmDelete(id: number) {
    const confirmDialogData: ConfirmationData = {
      title: "Confirm Deletion",
      confirmMessage: "Are you sure you want to delete this custome field?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      displayComment: false,
      isWarning: false,
    };

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      width: "500px",
      data: confirmDialogData,
    });

    confirmDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result === true) {
        this.deleteCustomField(id);
      }
    });
  }

  deleteCustomField(id: number){
    this.loading = true;
    this.varietyService.deleteCropVarietyCustomField(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastr.info("Custome Field successfully deleted!");
        this.variety.customFields = this.variety.customFields.filter(field => field.id !== id);
        this.cropLoading = false;
        this.ngOnInit();
      },
      error: (err) => {
        this.errorService.handleError(err);
        this.loading = false;
      }
    })
  }

  onCropChange(cropId: number) {
    let selectedCrop = this.crops.find(x => x.id == cropId);

    if (selectedCrop) {
      this.variety.cropName = selectedCrop.name;
    }
  }

  onSubmit() {
    const self = this;
    this.submitting = true;

    if (!self.variety.id || self.variety.id === 0) {
      // create variety — close immediately; VarietiesListComponent performs the POST in the background.
      self.dialogRef.close({
        backgroundSave: true,
        isNew: true,
        variety: { ...self.variety }
      });
      return;
    }

    // update variety — close immediately; VarietiesListComponent performs the PUT in the background.
    if (self.variety.customFields && self.variety.customFields.length > 0) {
      self.varietyService.putCustomField(self.variety.customFields).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (x) => {
          self.variety.customFields = self.mergeCustomFields(self.variety.customFields, x);
          self.dialogRef.close({
            backgroundSave: true,
            isNew: false,
            variety: { ...self.variety }
          });
        },
        error: (err) => {
          self.errorService.handleError(err);
          self.toastr.danger('Failed to save custom fields for "' + self.variety.name + '". Please try again.', "Save failed");
          self.submitting = false;
        }
      });
      return;
    }

    self.dialogRef.close({
      backgroundSave: true,
      isNew: false,
      variety: { ...self.variety }
    });
  }

  cancelClicked(cancelled: boolean = false) {
    const result = (this.isEditBtnClick && this.variety && this.variety.id)
      ? { customFieldsOnly: true, id: this.variety.id, customFields: this.variety.customFields }
      : cancelled;
    this.variety.isActive = false;
    this.variety = {};
    this.dialogRef.close(result);
  }

  onColorPickerToggle(isOpen: boolean) {
  if (isOpen) {
    this.isColorPickerOpen = !this.isColorPickerOpen;
  } else {
    this.isColorPickerOpen = !this.isColorPickerOpen;
  }
}


}
